import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  EyeIcon,
  EyeSlashIcon,
  Cog6ToothIcon,
  UserIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import TableColumnFilter from '../../components/TableColumnFilter';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const TYPE_FILTER_OPTIONS = [
  { value: 'SYSTEM', label: 'System' },
  { value: 'USER', label: 'User' },
];

const DATA_TYPE_FILTER_OPTIONS = [
  { value: 'STRING', label: 'String' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'BOOLEAN', label: 'Boolean' },
  { value: 'SECRET', label: 'Secret' },
];

const REQUIRED_FILTER_OPTIONS = [
  { value: 'yes', label: 'Required' },
  { value: 'no', label: 'Optional' },
];

interface Variable {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  type: 'SYSTEM' | 'USER';
  dataType: 'STRING' | 'NUMBER' | 'BOOLEAN' | 'SECRET';
  source?: string;
  defaultValue?: string;
  isRequired: boolean;
  isSecret: boolean;
  validationRule?: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const PipelineVariables: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allVariables, setAllVariables] = useState<Variable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [showForm, setShowForm] = useState(false);
  const [editingVariable, setEditingVariable] = useState<Variable | null>(null);

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '10', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 10;
  }, [searchParams]);

  const appliedSearch = searchParams.get('search') ?? '';
  const filterDisplayName = searchParams.get('displayName') ?? '';
  const filterVarName = searchParams.get('varName') ?? '';
  const filterType = searchParams.get('type') ?? '';
  const filterDataType = searchParams.get('dataType') ?? '';
  const filterRequired = searchParams.get('required') ?? '';
  const filterPurpose = searchParams.get('purpose') ?? '';

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  useEffect(() => {
    setSearchInput(appliedSearch);
  }, [appliedSearch]);

  useEffect(() => {
    const id = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed === appliedSearch) return;
      patchSearchParams({ search: trimmed || null, page: '1' });
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput, appliedSearch, patchSearchParams]);

  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    dataType: 'STRING' as 'STRING' | 'NUMBER' | 'BOOLEAN' | 'SECRET',
    defaultValue: '',
    isRequired: false,
    isSecret: false
  });

  useEffect(() => {
    loadVariables();
  }, []);

  const loadVariables = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/variable-templates');
      // Sort variables by type (SYSTEM first) then by sortOrder
      const sortedVariables = response.data.sort((a: Variable, b: Variable) => {
        if (a.type !== b.type) {
          return a.type === 'SYSTEM' ? -1 : 1;
        }
        return a.sortOrder - b.sortOrder;
      });
      setAllVariables(sortedVariables);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load pipeline variables');
      toast.error('Failed to load pipeline variables');
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering then pagination
  const filteredVariables = useMemo(() => {
    return allVariables.filter((v) => {
      if (appliedSearch) {
        const s = appliedSearch.toLowerCase();
        const matchesSearch =
          v.displayName.toLowerCase().includes(s) ||
          v.name.toLowerCase().includes(s) ||
          (v.description || '').toLowerCase().includes(s);
        if (!matchesSearch) return false;
      }
      if (filterDisplayName && !v.displayName.toLowerCase().includes(filterDisplayName.toLowerCase())) {
        return false;
      }
      if (filterVarName && !v.name.toLowerCase().includes(filterVarName.toLowerCase())) {
        return false;
      }
      if (filterType && v.type !== filterType) return false;
      if (filterDataType && v.dataType !== filterDataType) return false;
      if (filterRequired === 'yes' && !v.isRequired) return false;
      if (filterRequired === 'no' && v.isRequired) return false;
      if (filterPurpose && !(v.description || '').toLowerCase().includes(filterPurpose.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [allVariables, appliedSearch, filterDisplayName, filterVarName, filterType, filterDataType, filterRequired, filterPurpose]);

  const totalCount = filteredVariables.length;
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const variables = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return filteredVariables.slice(startIndex, startIndex + pageSize);
  }, [filteredVariables, page, pageSize]);

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'displayName':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterDisplayName}
              placeholder="Filter name..."
              onChange={(v) => {
                patchSearchParams({ displayName: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'varName':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterVarName}
              placeholder="Filter variable..."
              onChange={(v) => {
                patchSearchParams({ varName: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'type':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={TYPE_FILTER_OPTIONS}
              value={filterType}
              placeholder="All Types"
              onChange={(v) => {
                patchSearchParams({ type: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'dataType':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={DATA_TYPE_FILTER_OPTIONS}
              value={filterDataType}
              placeholder="All Data Types"
              onChange={(v) => {
                patchSearchParams({ dataType: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'required':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={REQUIRED_FILTER_OPTIONS}
              value={filterRequired}
              placeholder="All"
              onChange={(v) => {
                patchSearchParams({ required: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'purpose':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterPurpose}
              placeholder="Filter purpose..."
              onChange={(v) => {
                patchSearchParams({ purpose: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [filterDisplayName, filterVarName, filterType, filterDataType, filterRequired, filterPurpose, patchSearchParams]
  );

  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (appliedSearch) chips.push({ id: 'search', label: 'Search', value: appliedSearch, color: 'gray' });
    if (filterDisplayName) chips.push({ id: 'displayName', label: 'Name', value: filterDisplayName, color: 'blue' });
    if (filterVarName) chips.push({ id: 'varName', label: 'Variable Name', value: filterVarName, color: 'blue' });
    if (filterType) {
      chips.push({
        id: 'type',
        label: 'Variable Type',
        value: TYPE_FILTER_OPTIONS.find((o) => o.value === filterType)?.label || filterType,
        color: 'teal',
      });
    }
    if (filterDataType) {
      chips.push({
        id: 'dataType',
        label: 'Data Type',
        value: filterDataType,
        color: 'purple',
      });
    }
    if (filterRequired) {
      chips.push({
        id: 'required',
        label: 'Required',
        value: REQUIRED_FILTER_OPTIONS.find((o) => o.value === filterRequired)?.label || filterRequired,
        color: 'green',
      });
    }
    if (filterPurpose) chips.push({ id: 'purpose', label: 'Purpose', value: filterPurpose, color: 'blue' });
    return chips;
  }, [appliedSearch, filterDisplayName, filterVarName, filterType, filterDataType, filterRequired, filterPurpose]);

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'displayName':
        patchSearchParams({ displayName: null, page: '1' });
        break;
      case 'varName':
        patchSearchParams({ varName: null, page: '1' });
        break;
      case 'type':
        patchSearchParams({ type: null, page: '1' });
        break;
      case 'dataType':
        patchSearchParams({ dataType: null, page: '1' });
        break;
      case 'required':
        patchSearchParams({ required: null, page: '1' });
        break;
      case 'purpose':
        patchSearchParams({ purpose: null, page: '1' });
        break;
    }
  };

  const clearAllFilters = () => {
    setSearchInput('');
    patchSearchParams({
      search: null,
      displayName: null,
      varName: null,
      type: null,
      dataType: null,
      required: null,
      purpose: null,
      page: '1',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        type: 'USER', // Users can only create USER variables
        sortOrder: allVariables.filter(v => v.type === 'USER').length + 100, // Put user variables after system ones
        id: editingVariable?.id
      };

      const response = await api.post('/variable-templates', data);
      
      if (response.data.success) {
        toast.success(editingVariable ? 'Variable updated successfully' : 'Variable created successfully');
        await loadVariables();
        resetForm();
      }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to save variable';
      toast.error(message);
    }
  };

  const handleEdit = (variable: Variable) => {
    if (variable.type === 'SYSTEM') {
      toast.error('System variables cannot be edited');
      return;
    }
    
    setEditingVariable(variable);
    setFormData({
      name: variable.name,
      displayName: variable.displayName,
      description: variable.description || '',
      dataType: variable.dataType,
      defaultValue: variable.defaultValue || '',
      isRequired: variable.isRequired,
      isSecret: variable.isSecret
    });
    setShowForm(true);
  };

  const handleDelete = async (variable: Variable) => {
    if (variable.type === 'SYSTEM') {
      toast.error('System variables cannot be deleted');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the variable "${variable.displayName}"?`)) {
      return;
    }

    try {
      const response = await api.delete(`/variable-templates/${variable.id}`);
      if (response.data.success) {
        toast.success('Variable deleted successfully');
        await loadVariables();
      }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to delete variable';
      toast.error(message);
    }
  };

  const resetForm = () => {
    setEditingVariable(null);
    setFormData({
      name: '',
      displayName: '',
      description: '',
      dataType: 'STRING',
      defaultValue: '',
      isRequired: false,
      isSecret: false
    });
    setShowForm(false);
  };

  const toggleSecretVisibility = (variableId: string) => {
    setShowSecrets(prev => ({
      ...prev,
      [variableId]: !prev[variableId]
    }));
  };

  const getTypeIcon = (type: string) => {
    return type === 'SYSTEM' ? (
      <Cog6ToothIcon className="h-5 w-5 text-blue-500" />
    ) : (
      <UserIcon className="h-5 w-5 text-green-500" />
    );
  };

  const getDataTypeColor = (dataType: string) => {
    switch (dataType) {
      case 'SECRET': return 'bg-red-100 text-red-800';
      case 'NUMBER': return 'bg-blue-100 text-blue-800';
      case 'BOOLEAN': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading pipeline variables...</span>
      </div>
    );
  }

  // Variables are available in the variables state

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Pipeline Variables</h1>
              <p className="text-xs text-gray-500">Manage variables passed to Azure DevOps pipeline execution</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search variables..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-8 pr-3 py-1.5 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center px-3 py-1.5 text-sm border border-transparent rounded-md shadow-sm font-medium text-white bg-blue-600 hover:bg-blue-700 shrink-0"
              >
                <PlusIcon className="h-4 w-4 mr-1.5" />
                Add User Variable
              </button>
            </div>
          </div>
          {activeFilterChips.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <ActiveFilterChips
                chips={activeFilterChips}
                onRemove={handleRemoveFilterChip}
                onClearAll={clearAllFilters}
              />
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-2 flex-shrink-0">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingVariable ? 'Edit User Variable' : 'Add User Variable'}
                </h3>
                <button
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Variable Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="my_custom_variable"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">Use lowercase with underscores</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Display Name</label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="My Custom Variable"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    rows={2}
                    placeholder="What this variable is used for"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Data Type</label>
                  <select
                    value={formData.dataType}
                    onChange={(e) => setFormData({ ...formData, dataType: e.target.value as any })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="STRING">String</option>
                    <option value="NUMBER">Number</option>
                    <option value="BOOLEAN">Boolean</option>
                    <option value="SECRET">Secret</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Default Value</label>
                  <input
                    type={formData.isSecret ? 'password' : 'text'}
                    value={formData.defaultValue}
                    onChange={(e) => setFormData({ ...formData, defaultValue: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Default value for this variable"
                  />
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isRequired}
                      onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Required</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isSecret}
                      onChange={(e) => setFormData({ ...formData, isSecret: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Secret</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {editingVariable ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg flex flex-col flex-1 min-h-0">
        {filteredVariables.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {activeFilterChips.length > 0 ? 'No variables match your filters.' : 'No variables found'}
          </div>
        ) : (
          <div className="overflow-x-auto flex-1">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 w-40 bg-gray-50">
                    {renderColumnHeader('displayName', 'Name')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 w-44 bg-gray-50">
                    {renderColumnHeader('varName', 'Variable Name')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 w-24 bg-gray-50">
                    {renderColumnHeader('type', 'Variable Type')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 w-20 bg-gray-50">
                    {renderColumnHeader('dataType', 'Data Type')}
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 w-20 bg-gray-50">
                    {renderColumnHeader('required', 'Required')}
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 bg-gray-50">
                    {renderColumnHeader('purpose', 'Purpose')}
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20 bg-gray-50">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {variables.map((variable) => (
                  <tr key={variable.id} className="hover:bg-gray-50">
                    {/* Name Column */}
                    <td className="px-3 py-4 whitespace-nowrap border-r border-gray-200">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          {getTypeIcon(variable.type)}
                        </div>
                        <div className="ml-2">
                          <div className="text-sm font-medium text-gray-900">
                            {variable.displayName}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    {/* Variable Name Column */}
                    <td className="px-3 py-4 whitespace-nowrap border-r border-gray-200">
                      <div className="text-xs font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded">
                        {variable.name}
                      </div>
                    </td>
                    
                    {/* Variable Type Column */}
                    <td className="px-3 py-4 whitespace-nowrap border-r border-gray-200">
                      {variable.type === 'SYSTEM' && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          System
                        </span>
                      )}
                      {variable.type === 'USER' && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          User
                        </span>
                      )}
                    </td>
                    
                    {/* Data Type Column */}
                    <td className="px-3 py-4 whitespace-nowrap border-r border-gray-200">
                      <span className={`inline-flex items-center px-1 py-1 rounded-full text-xs font-medium ${getDataTypeColor(variable.dataType)}`}>
                        {variable.dataType}
                      </span>
                    </td>
                    
                    {/* Required Column */}
                    <td className="px-3 py-4 whitespace-nowrap border-r border-gray-200">
                      <div className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={variable.isRequired}
                          disabled
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>
                    </td>
                    
                    {/* Purpose Column */}
                    <td className="px-3 py-4 border-r border-gray-200">
                      <div className="text-sm text-gray-900">
                        {variable.description || 'No description provided'}
                      </div>
                      
                      {variable.defaultValue && (
                        <div className="text-xs text-gray-500 mt-1">
                          <strong>Default:</strong>{' '}
                          {variable.isSecret ? (
                            <div className="inline-flex items-center space-x-1">
                              <span className="font-mono">
                                {showSecrets[variable.id] 
                                  ? variable.defaultValue
                                  : '••••••••'
                                }
                              </span>
                              <button
                                onClick={() => toggleSecretVisibility(variable.id)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                {showSecrets[variable.id] ? (
                                  <EyeSlashIcon className="h-3 w-3" />
                                ) : (
                                  <EyeIcon className="h-3 w-3" />
                                )}
                              </button>
                            </div>
                          ) : (
                            <span className="font-mono">{variable.defaultValue}</span>
                          )}
                        </div>
                      )}
                    </td>
                    
                    {/* Actions Column */}
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center space-x-2">
                        {variable.type === 'USER' ? (
                          <>
                            <button
                              onClick={() => handleEdit(variable)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit Variable"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(variable)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Variable"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Enhanced Pagination */}
        {!loading && totalCount > 0 && (
          <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
            {/* Left: Records per page dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Show</span>
              <select
                value={pageSize}
                onChange={(e) => patchSearchParams({ pageSize: e.target.value, page: '1' })}
                className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {PAGE_SIZE_OPTIONS.map((size, index) => {
                  const previousSize = index > 0 ? PAGE_SIZE_OPTIONS[index - 1] : 0;
                  const isDisabled = previousSize >= totalCount && size > totalCount;
                  return (
                    <option key={size} value={size} disabled={isDisabled}>
                      {size}
                    </option>
                  );
                })}
              </select>
              <span className="text-sm text-gray-700">entries</span>
            </div>

            {/* Center: Entry count */}
            <div className="text-sm text-gray-700">
              Showing {Math.min((page - 1) * pageSize + 1, totalCount)} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
            </div>

            {/* Right: Pagination buttons */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => patchSearchParams({ page: '1' })}
                disabled={page === 1}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First page"
              >
                &laquo;
              </button>
              <button
                onClick={() => patchSearchParams({ page: String(Math.max(1, page - 1)) })}
                disabled={page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {/* Page numbers */}
              {(() => {
                const pages: (number | string)[] = [];

                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else {
                  if (page <= 4) {
                    for (let i = 1; i <= 5; i++) pages.push(i);
                    pages.push('...');
                    pages.push(totalPages);
                  } else if (page >= totalPages - 3) {
                    pages.push(1);
                    pages.push('...');
                    for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
                  } else {
                    pages.push(1);
                    pages.push('...');
                    for (let i = page - 1; i <= page + 1; i++) pages.push(i);
                    pages.push('...');
                    pages.push(totalPages);
                  }
                }

                return pages.map((p, idx) =>
                  p === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-3 py-1 text-sm text-gray-500">
                      ...
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => patchSearchParams({ page: String(p) })}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        page === p
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  )
                );
              })()}

              <button
                onClick={() => patchSearchParams({ page: String(Math.min(totalPages, page + 1)) })}
                disabled={page >= totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => patchSearchParams({ page: String(totalPages) })}
                disabled={page >= totalPages}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last page"
              >
                &raquo;
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PipelineVariables;